import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { quotesAPI, clientsAPI, inventoryAPI, taxesAPI, accountStatusesAPI, hrAPI } from '../lib/api';
import CatalogItemPicker, { EMPTY_ITEM as CATALOG_EMPTY_ITEM } from '../components/CatalogItemPicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '../components/ui/command';
import { Plus, Search, Edit, Trash2, X, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { numberToWords } from '../utils/numberToWords';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

// Taxes and inventory will be loaded from database

export default function Quotes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const currency = useCurrency();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [inventoryItemsFromDB, setInventoryItemsFromDB] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [quoteStatuses, setQuoteStatuses] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [clientComboboxOpen, setClientComboboxOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Removed dummy clients - only use real database data
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    quote_number: '',
    quote_date: format(new Date(), 'yyyy-MM-dd'),
    expiry_date: '',
    status: 'draft',
    discount_type: 'percentage',
    discount_value: 0,
    tax_rate: 0,
    tax_name: '',
    deposit_required: 0,
    instructions: '',
    notes: '',
    terms: '',
  });
  
  const [lineItems, setLineItems] = useState([CATALOG_EMPTY_ITEM()]);
  
  // (inventoryItems removed — unified into catalog-based lineItems)

  // Permission check functions
  const canUpdate = () => {
    if (!user?.module_permissions) return true; // Admin has all permissions
    return hasPermission('account', 'quote', 'update');
  };

  const canDelete = () => {
    if (!user?.module_permissions) return true; // Admin has all permissions
    return hasPermission('account', 'quote', 'delete');
  };

  useEffect(() => {
    loadData();
  }, [searchTerm, statusFilter, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      // Build query params
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const [quotesRes, clientsRes, inventoryRes, taxesRes, quoteStatusesRes] = await Promise.all([
        quotesAPI.getAll(params),
        clientsAPI.getAll(),  // Get all clients (admin sees all, team sees own + admin's)
        inventoryAPI.getItems({}),  // All inventory items (admin + user)
        taxesAPI.getAll(),  // All taxes from database
        accountStatusesAPI.getQuoteStatuses()  // All quote statuses (fixed + custom)
      ]);
      
      // Filter by date range on frontend (if backend doesn't support it)
      let filteredQuotes = quotesRes.data?.items || quotesRes.data || [];
      if (dateFrom || dateTo) {
        filteredQuotes = filteredQuotes.filter(quote => {
          const quoteDate = new Date(quote.quote_date);
          if (dateFrom && quoteDate < new Date(dateFrom)) return false;
          if (dateTo && quoteDate > new Date(dateTo)) return false;
          return true;
        });
      }
      
      // Sort by updated_at or created_at (newest first)
      filteredQuotes.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at);
        const dateB = new Date(b.updated_at || b.created_at);
        return dateB - dateA;  // Descending order (newest first)
      });
      
      setQuotes(filteredQuotes);
      
      // Use only real clients from database
      const fetchedClients = clientsRes.data || [];
      // Sort clients by newest first (updated_at or created_at)
      fetchedClients.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;  // Descending order (newest first)
      });
      setClients(fetchedClients);
      
      // Use only real inventory items from database
      const fetchedInventory = inventoryRes.data || [];
      setInventoryItemsFromDB(fetchedInventory);
      
      // Use only real taxes from database
      const fetchedTaxes = taxesRes.data?.items || taxesRes.data || [];
      setTaxes(fetchedTaxes);
      
      // Use only real quote statuses from database
      const fetchedQuoteStatuses = quoteStatusesRes.data || [];
      setQuoteStatuses(fetchedQuoteStatuses);
      
      // Load team members for Created By column
      try {
        const teamRes = await hrAPI.getTeamMembers();
        setTeamMembers(teamRes.data || []);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getCreatorName = (createdBy) => {
    const member = teamMembers.find(m => m.id === createdBy);
    return member ? member.name : 'Unknown';
  };

  // Calculate Totals (from unified catalog-based line items)
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
  };

  const calculateTax = () => {
    const itemTax = lineItems.reduce((sum, item) => {
      const subtotal = (item.quantity || 0) * (item.price || 0);
      return sum + subtotal * ((item.taxRate || 0) / 100);
    }, 0);
    const subtotal = calculateSubtotal();
    const additionalTax = subtotal * (formData.tax_rate || 0) / 100;
    return itemTax + additionalTax;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (formData.discount_type === 'percentage') {
      return subtotal * (formData.discount_value || 0) / 100;
    }
    return formData.discount_value || 0;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + tax - discount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    
    // Comprehensive validation with specific error messages
    const errors = {};
    
    if (!formData.client_id) {
      errors.client_id = 'Client is required';
    }
    
    if (!formData.quote_date) {
      errors.quote_date = 'Quote Date is required';
    }

    // Check for duplicate quote number
    if (formData.quote_number) {
      const isDuplicate = quotes.some(q => 
        q.quote_number === formData.quote_number && 
        (!editingQuote || q.id !== editingQuote.id)
      );
      
      if (isDuplicate) {
        errors.quote_number = `Quote number "${formData.quote_number}" already exists`;
      }
    }
    
    setFormErrors(errors);
    
    // Show validation errors if any
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach(err => toast.error(err));
      return;
    }

    const grandTotal = calculateGrandTotal();
    const quoteData = {
      ...formData,
      // Convert empty date strings to null for backend validation
      expiry_date: formData.expiry_date || null,
      line_items: lineItems.filter(it => it.description?.trim() || it.catalog_item_id).map(it => ({
        catalog_item_id: it.catalog_item_id || null,
        description: it.description,
        quantity: it.quantity || 1,
        price: it.price || 0,
        tax: ((it.quantity || 0) * (it.price || 0)) * ((it.taxRate || 0) / 100),
        taxRate: it.taxRate || 0,
        total: ((it.quantity || 0) * (it.price || 0)) * (1 + (it.taxRate || 0) / 100),
        hsn_sac: it.hsn_sac || null,
      })),
      inventory_items: [],
      subtotal: calculateSubtotal(),
      tax_amount: calculateTax(),
      discount_amount: calculateDiscount(),
      total_amount: grandTotal,
      total_in_words: numberToWords(grandTotal, currency.currencyCode)
    };

    try {
      if (editingQuote) {
        await quotesAPI.update(editingQuote.id, quoteData);
        toast.success('Quote updated successfully');
      } else {
        const response = await quotesAPI.create(quoteData);
        console.log('Quote created:', response.data);
        toast.success('Quote created successfully');
      }
      setSheetOpen(false);
      resetForm();
      await loadData(); // Wait for data to load before closing
    } catch (error) {
      console.error('Failed to save quote:', error);
      
      // Parse and display meaningful error messages
      let errorMessage = 'Failed to create quote';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle Pydantic validation errors (array format)
        if (Array.isArray(detail)) {
          const fieldErrors = detail.map(err => {
            const field = err.loc?.join(' > ') || 'Unknown field';
            const msg = err.msg || 'Invalid value';
            return `${field}: ${msg}`;
          });
          errorMessage = fieldErrors.join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 5000,
        description: 'Please check the form fields and try again'
      });
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      client_id: quote.client_id || '',
      quote_number: quote.quote_number || '',
      quote_date: quote.quote_date ? format(new Date(quote.quote_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      expiry_date: quote.expiry_date ? format(new Date(quote.expiry_date), 'yyyy-MM-dd') : '',
      status: quote.status || 'draft',
      discount_type: quote.discount_type || 'percentage',
      discount_value: quote.discount_value || 0,
      tax_rate: quote.tax_rate || 0,
      tax_name: quote.tax_name || '',
      deposit_required: quote.deposit_required || 0,
      instructions: quote.instructions || '',
      notes: quote.notes || '',
      terms: quote.terms || '',
    });
    // Convert existing line items + inventory items into unified catalog picker format
    const existingLineItems = (quote.line_items || []).map(it => ({
      _key: Date.now() + Math.random(),
      catalog_item_id: it.catalog_item_id || null,
      description: it.description || '',
      quantity: it.quantity || 1,
      price: it.price || 0,
      taxRate: it.taxRate || it.tax_rate || 0,
      hsn_sac: it.hsn_sac || '',
    }));
    const existingInvItems = (quote.inventory_items || []).map(it => ({
      _key: Date.now() + Math.random(),
      catalog_item_id: it.itemId || it.catalog_item_id || null,
      description: it.name || it.description || '',
      quantity: it.quantity || 1,
      price: it.price || 0,
      taxRate: it.taxRate || it.tax_rate || 0,
      hsn_sac: it.hsn_sac || '',
    }));
    const merged = [...existingLineItems, ...existingInvItems];
    setLineItems(merged.length > 0 ? merged : [CATALOG_EMPTY_ITEM()]);
    setSheetOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        await quotesAPI.delete(id);
        toast.success('Quote deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete quote');
      }
    }
  };

  const resetForm = () => {
    setEditingQuote(null);
    setFormData({
      client_id: '',
      quote_number: '',
      quote_date: format(new Date(), 'yyyy-MM-dd'),
      expiry_date: '',
      status: 'draft',
      discount_type: 'percentage',
      discount_value: 0,
      tax_rate: 0,
      tax_name: '',
      deposit_required: 0,
      instructions: '',
      notes: '',
      terms: '',
    });
    setLineItems([CATALOG_EMPTY_ITEM()]);
    setClientSearch('');
    setFormErrors({});
    setSubmitAttempted(false);
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.company_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const subtotal = calculateSubtotal();
  const tax = calculateTax();
  const discount = calculateDiscount();
  const grandTotal = calculateGrandTotal();

  return (
    <div className="space-y-4" data-testid="quotes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Quotes</h1>
          <p className="text-sm text-muted-foreground">Manage and track your sales quotes</p>
        </div>
        {hasPermission('account', 'quote', 'create') && (
          <Sheet open={sheetOpen} onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) resetForm();
          }}>
            <SheetTrigger asChild>
              <Button data-testid="create-quote-button">
                <Plus className="h-4 w-4 mr-2" />
                Create Quote
              </Button>
            </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto" data-testid="quote-sheet">
            <SheetHeader>
              <SheetTitle>{editingQuote ? 'Edit Quote' : 'Create New Quote'}</SheetTitle>
            </SheetHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className={formErrors.client_id && submitAttempted ? 'text-destructive' : ''}>Client *</Label>
                    <Popover open={clientComboboxOpen && !editingQuote} onOpenChange={!editingQuote ? setClientComboboxOpen : undefined}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={clientComboboxOpen}
                          className={`w-full justify-between ${formErrors.client_id && submitAttempted ? 'border-destructive ring-1 ring-destructive' : ''}`}
                          data-testid="client-select"
                          disabled={!!editingQuote}
                        >
                          {formData.client_id
                            ? clients.find((client) => client.id === formData.client_id)?.name || "Select client"
                            : "Search and select client..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0">
                        <Command>
                          <CommandInput placeholder="Search clients..." data-testid="client-search-input" />
                          <CommandEmpty>No client found.</CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            {clients.map((client) => (
                              <CommandItem
                                key={client.id}
                                value={`${client.name} ${client.email} ${client.company_name || ''}`}
                                onSelect={() => {
                                  setFormData({ ...formData, client_id: client.id });
                                  setClientComboboxOpen(false);
                                  if (formErrors.client_id) {
                                    setFormErrors({ ...formErrors, client_id: null });
                                  }
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    formData.client_id === client.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{client.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {client.company_name && `${client.company_name} • `}{client.email}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {formErrors.client_id && submitAttempted && (
                      <p className="text-sm text-destructive mt-1">{formErrors.client_id}</p>
                    )}
                  </div>
                  <div>
                    <Label>Quote Number</Label>
                    <Input
                      placeholder="Auto-generated if empty"
                      value={formData.quote_number}
                      onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                      data-testid="quote-number-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Quote Date *</Label>
                    <Input
                      type="date"
                      value={formData.quote_date}
                      onChange={(e) => setFormData({ ...formData, quote_date: e.target.value })}
                      required
                      data-testid="quote-date-input"
                    />
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                      data-testid="expiry-date-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {quoteStatuses.map((status) => (
                          <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Line Items — unified catalog picker */}
              <CatalogItemPicker lineItems={lineItems} setLineItems={setLineItems} currency={currency} />

              {/* Taxes and Discounts */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Taxes & Discounts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tax</Label>
                    <Select
                      value={`${formData.tax_rate}`}
                      onValueChange={(value) => {
                        const selectedTax = taxes.find(t => t.rate.toString() === value);
                        setFormData({ 
                          ...formData, 
                          tax_rate: selectedTax ? parseFloat(selectedTax.rate) : 0,
                          tax_name: selectedTax ? selectedTax.name : ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="tax-select">
                        <SelectValue placeholder="Select tax" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Tax</SelectItem>
                        {taxes.map((tax) => (
                          <SelectItem key={tax.id} value={tax.rate.toString()}>
                            {tax.name} ({tax.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={formData.discount_type} onValueChange={(value) => setFormData({ ...formData, discount_type: value })}>
                      <SelectTrigger data-testid="discount-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Discount Value</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      data-testid="discount-value-input"
                    />
                  </div>
                  <div>
                    <Label>Required Deposit ($)</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={formData.deposit_required}
                      onChange={(e) => setFormData({ ...formData, deposit_required: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                      data-testid="deposit-input"
                    />
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              {lineItems.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">{currency.format(subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax ({formData.tax_name || 'N/A'}):</span>
                        <span className="font-medium">{currency.format(tax)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Discount ({formData.discount_type === 'percentage' ? `${formData.discount_value}%` : '$' + formData.discount_value}):
                        </span>
                        <span className="font-medium text-green-600">-{currency.format(discount)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-lg font-bold">Grand Total:</span>
                        <span className="text-lg font-bold text-primary">{currency.format(grandTotal)}</span>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Amount in Words:</p>
                        <p className="text-sm font-medium italic">{numberToWords(grandTotal, currency.currencyCode)}</p>
                      </div>
                      {formData.deposit_required > 0 && (
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-sm font-medium">Required Deposit:</span>
                          <span className="text-sm font-bold text-orange-600">{currency.format(formData.deposit_required)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instructions and Notes */}
              <div className="space-y-4">
                <div>
                  <Label>Instructions (Customer-facing)</Label>
                  <Textarea
                    placeholder="Special instructions for the client..."
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={3}
                    data-testid="instructions-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      placeholder="Internal notes (not visible to client)..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      data-testid="notes-input"
                    />
                  </div>
                  <div>
                    <Label>Terms & Conditions</Label>
                    <Textarea
                      placeholder="Payment terms, delivery terms, etc..."
                      value={formData.terms}
                      onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                      rows={2}
                      data-testid="terms-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setSheetOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-quote-button">
                  {editingQuote ? 'Update Quote' : 'Create Quote'}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
        )}
      </div>

      {/* Filters Section */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                placeholder="Search by quote number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="quote-search-input"
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
                <SelectTrigger data-testid="status-filter-select">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {quoteStatuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-input"
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-input"
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter || dateFrom || dateTo) && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
                data-testid="clear-filters-button"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">S.No</TableHead>
              <TableHead>Quote #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="p-0">
                  <div className="space-y-0">
                    {[...Array(5)].map((_, rowIndex) => (
                      <div key={rowIndex} className="flex gap-4 p-4 border-b last:border-b-0">
                        {[...Array(9)].map((_, colIndex) => (
                          <div key={colIndex} className={`h-4 flex-1 bg-muted/60 rounded animate-pulse ${colIndex === 0 ? 'max-w-[50px]' : ''} ${colIndex === 8 ? 'max-w-[80px]' : ''}`} />
                        ))}
                      </div>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ) : quotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No quotes found
                </TableCell>
              </TableRow>
            ) : (
              quotes.map((quote, index) => (
                <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                  <TableCell className="text-muted-foreground">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                  <TableCell className="font-medium">
                    <button
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      className="text-primary hover:underline focus:outline-none"
                      data-testid={`quote-number-${quote.id}`}
                    >
                      {quote.quote_number || quote.id.slice(0, 8)}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => quote.client_id && navigate(`/clients/${quote.client_id}`)}
                      className="text-primary hover:underline focus:outline-none"
                      data-testid={`client-name-${quote.id}`}
                    >
                      {quote.client_name || quote.client_id || 'Unknown Client'}
                    </button>
                  </TableCell>
                  <TableCell>
                    {quote.quote_date ? format(new Date(quote.quote_date), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    {quote.expiry_date ? format(new Date(quote.expiry_date), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell className="font-semibold">{currency.format(parseFloat(quote.total_amount || 0))}</TableCell>
                  <TableCell>
                    <Badge 
                      style={{
                        backgroundColor: quoteStatuses.find(s => s.name.toLowerCase() === quote.status?.toLowerCase())?.color || '#gray',
                        color: '#fff'
                      }}
                    >
                      {quoteStatuses.find(s => s.name.toLowerCase() === quote.status?.toLowerCase())?.name || 
                       quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {quote.created_by_name || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canUpdate() && quote.status?.toLowerCase() !== 'schedule job' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(quote)}
                          data-testid={`edit-quote-${quote.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(quote.id)}
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
